import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function Privacy() {
  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-purple-600 to-purple-800 text-white py-20 pt-36">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="w-20 h-20 flex items-center justify-center bg-white/20 rounded-full text-5xl mb-6">
            <i className="ri-shield-user-line"></i>
          </div>
          <h1 className="text-5xl font-bold mb-6">Privacy Policy</h1>
          <p className="text-xl text-white/90">
            Last Updated: January 2026
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 flex-grow">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            
            {/* Introduction */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">1. Introduction</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                At Stashly, we take your privacy seriously. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our platform. Please read this privacy policy carefully.
              </p>
              <p className="text-gray-700 leading-relaxed">
                By using Stashly, you agree to the collection and use of information in accordance with this policy. If you do not agree with our policies and practices, please do not use our platform.
              </p>
            </div>

            {/* Information We Collect */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">2. Information We Collect</h2>
              
              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Personal Information</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                We collect personal information that you voluntarily provide to us when you:
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-3">
                  <i className="ri-user-line text-[#14B8A6] text-xl mt-1"></i>
                  <span className="text-gray-700">Register for an account (name, email address, phone number, date of birth)</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-bank-card-line text-[#14B8A6] text-xl mt-1"></i>
                  <span className="text-gray-700">Make a booking or list a space (payment information, address, identification documents)</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-message-line text-[#14B8A6] text-xl mt-1"></i>
                  <span className="text-gray-700">Communicate with us (messages, reviews, support inquiries)</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-camera-line text-[#14B8A6] text-xl mt-1"></i>
                  <span className="text-gray-700">Upload content (profile photos, space photos, verification documents)</span>
                </li>
              </ul>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-8">Automatically Collected Information</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                When you access our platform, we automatically collect certain information:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-computer-line text-blue-600 text-xl mt-1"></i>
                  <span className="text-gray-700">Device information (IP address, browser type, operating system)</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-map-pin-line text-blue-600 text-xl mt-1"></i>
                  <span className="text-gray-700">Location data (with your permission)</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-line-chart-line text-blue-600 text-xl mt-1"></i>
                  <span className="text-gray-700">Usage data (pages visited, features used, time spent on platform)</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-cookie-line text-blue-600 text-xl mt-1"></i>
                  <span className="text-gray-700">Cookies and similar tracking technologies</span>
                </li>
              </ul>
            </div>

            {/* Identity Verification Section - NEW */}
            <div className="mb-12 bg-blue-50 border-l-4 border-blue-600 p-8 rounded-r-xl">
              <h2 className="text-3xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                <i className="ri-shield-check-line text-blue-600"></i>
                3. Identity Verification
              </h2>
              
              <p className="text-gray-700 leading-relaxed mb-6">
                To maintain trust and safety on our platform, we may require users to verify their identity. This section explains how we collect, use, store, and protect your verification documents.
              </p>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-6">What We Collect for Verification</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                When you choose to verify your identity, we may collect:
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-3">
                  <i className="ri-id-card-line text-blue-600 text-xl mt-1"></i>
                  <span className="text-gray-700"><strong>Government-Issued ID:</strong> Driver's license, passport, or national ID card (front and back)</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-file-text-line text-blue-600 text-xl mt-1"></i>
                  <span className="text-gray-700"><strong>Proof of Address:</strong> Utility bill, bank statement, or government letter dated within the last 3 months</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-user-smile-line text-blue-600 text-xl mt-1"></i>
                  <span className="text-gray-700"><strong>Selfie Photo:</strong> A clear photo of your face for identity matching purposes</span>
                </li>
              </ul>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-6">How We Use Verification Documents</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Your verification documents are used exclusively to:
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-green-600 text-xl mt-1"></i>
                  <span className="text-gray-700">Confirm your identity and age</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-green-600 text-xl mt-1"></i>
                  <span className="text-gray-700">Prevent fraud and unauthorized access</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-green-600 text-xl mt-1"></i>
                  <span className="text-gray-700">Comply with legal and regulatory requirements</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-green-600 text-xl mt-1"></i>
                  <span className="text-gray-700">Build trust within our community</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-green-600 text-xl mt-1"></i>
                  <span className="text-gray-700">Protect against illegal activities</span>
                </li>
              </ul>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-6">How We Protect Your Documents</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <div className="bg-white p-5 rounded-lg border border-blue-200">
                  <i className="ri-lock-2-line text-2xl text-blue-600 mb-2"></i>
                  <h4 className="font-bold text-gray-900 mb-2">End-to-End Encryption</h4>
                  <p className="text-gray-700 text-sm">All documents are encrypted during upload, storage, and transmission using industry-standard AES-256 encryption</p>
                </div>
                <div className="bg-white p-5 rounded-lg border border-blue-200">
                  <i className="ri-shield-keyhole-line text-2xl text-blue-600 mb-2"></i>
                  <h4 className="font-bold text-gray-900 mb-2">Restricted Access</h4>
                  <p className="text-gray-700 text-sm">Only authorized administrators can access verification documents, and all access is logged and monitored</p>
                </div>
                <div className="bg-white p-5 rounded-lg border border-blue-200">
                  <i className="ri-server-line text-2xl text-blue-600 mb-2"></i>
                  <h4 className="font-bold text-gray-900 mb-2">Secure Storage</h4>
                  <p className="text-gray-700 text-sm">Documents are stored on secure, encrypted servers with multiple layers of security protection</p>
                </div>
                <div className="bg-white p-5 rounded-lg border border-blue-200">
                  <i className="ri-file-shield-line text-2xl text-blue-600 mb-2"></i>
                  <h4 className="font-bold text-gray-900 mb-2">Audit Trail</h4>
                  <p className="text-gray-700 text-sm">Every access and action related to your documents is logged with timestamps for security and compliance</p>
                </div>
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-6">Document Retention and Deletion</h3>
              <div className="bg-white p-6 rounded-lg border border-blue-200 mb-6">
                <div className="flex items-start gap-4">
                  <i className="ri-time-line text-3xl text-blue-600 mt-1"></i>
                  <div>
                    <h4 className="font-bold text-gray-900 mb-3">Automatic 30-Day Deletion Policy</h4>
                    <p className="text-gray-700 mb-3">
                      Once your identity is verified, your verification documents are automatically scheduled for deletion 30 days after approval. This ensures your sensitive information is not retained longer than necessary.
                    </p>
                    <ul className="space-y-2 text-gray-700 text-sm">
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-right-s-line text-blue-600 mt-0.5"></i>
                        <span><strong>Day 0:</strong> You submit verification documents</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-right-s-line text-blue-600 mt-0.5"></i>
                        <span><strong>Day 1-2:</strong> Our team reviews your documents (typically 24-48 hours)</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-right-s-line text-blue-600 mt-0.5"></i>
                        <span><strong>Day 2:</strong> Verification approved - 30-day deletion timer starts</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <i className="ri-arrow-right-s-line text-blue-600 mt-0.5"></i>
                        <span><strong>Day 32:</strong> All verification documents are permanently deleted</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-6">Your Rights Regarding Verification Documents</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                You have the following rights concerning your verification documents:
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-3">
                  <i className="ri-delete-bin-line text-red-600 text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Request Immediate Deletion:</strong>
                    <span className="text-gray-700"> You can request deletion of your verification documents at any time, even before the 30-day period. We will process your request within 24 hours.</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-eye-line text-blue-600 text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Access Your Documents:</strong>
                    <span className="text-gray-700"> You can request copies of the verification documents we hold about you.</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-information-line text-blue-600 text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Verification Status:</strong>
                    <span className="text-gray-700"> You can view your verification status and submission history in your account settings.</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-circle-line text-orange-600 text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Withdraw Consent:</strong>
                    <span className="text-gray-700"> You can withdraw your consent for verification at any time, though this may limit your access to certain platform features.</span>
                  </div>
                </li>
              </ul>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-6">Third-Party Verification Services</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                We may use trusted third-party identity verification services (such as Stripe Identity) to process your verification. When we do:
              </p>
              <ul className="space-y-3 mb-6">
                <li className="flex items-start gap-3">
                  <i className="ri-shield-check-line text-green-600 text-xl mt-1"></i>
                  <span className="text-gray-700">These services are bound by strict data protection agreements</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-shield-check-line text-green-600 text-xl mt-1"></i>
                  <span className="text-gray-700">They only process data for verification purposes</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-shield-check-line text-green-600 text-xl mt-1"></i>
                  <span className="text-gray-700">They do not retain your documents after verification is complete</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-shield-check-line text-green-600 text-xl mt-1"></i>
                  <span className="text-gray-700">They comply with international data protection standards (GDPR, CCPA, etc.)</span>
                </li>
              </ul>

              <h3 className="text-2xl font-bold text-gray-900 mb-4 mt-6">Consent and Transparency</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Before submitting verification documents, you will be asked to provide explicit consent. This consent includes:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-line text-blue-600 text-xl mt-1"></i>
                  <span className="text-gray-700">Understanding that your documents will be securely stored</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-line text-blue-600 text-xl mt-1"></i>
                  <span className="text-gray-700">Acknowledgment of the 30-day automatic deletion policy</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-line text-blue-600 text-xl mt-1"></i>
                  <span className="text-gray-700">Awareness of your right to request immediate deletion</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-line text-blue-600 text-xl mt-1"></i>
                  <span className="text-gray-700">Agreement to our verification process and data handling practices</span>
                </li>
              </ul>
            </div>

            {/* How We Use Your Information */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">4. How We Use Your Information</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We use the information we collect for various purposes:
              </p>
              
              <div className="space-y-6">
                <div className="bg-green-50 border-l-4 border-green-600 p-6 rounded-r-lg">
                  <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <i className="ri-service-line text-green-600"></i>
                    To Provide and Maintain Our Service
                  </h4>
                  <p className="text-gray-700">Process bookings, facilitate payments, enable communication between users, and provide customer support</p>
                </div>

                <div className="bg-blue-50 border-l-4 border-blue-600 p-6 rounded-r-lg">
                  <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <i className="ri-shield-check-line text-blue-600"></i>
                    To Ensure Safety and Security
                  </h4>
                  <p className="text-gray-700">Verify user identities, detect and prevent fraud, enforce our terms and policies, and protect against illegal activities</p>
                </div>

                <div className="bg-purple-50 border-l-4 border-purple-600 p-6 rounded-r-lg">
                  <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <i className="ri-line-chart-line text-purple-600"></i>
                    To Improve Our Platform
                  </h4>
                  <p className="text-gray-700">Analyze usage patterns, conduct research, develop new features, and enhance user experience</p>
                </div>

                <div className="bg-orange-50 border-l-4 border-orange-600 p-6 rounded-r-lg">
                  <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <i className="ri-mail-line text-orange-600"></i>
                    To Communicate With You
                  </h4>
                  <p className="text-gray-700">Send booking confirmations, updates, promotional materials, and respond to your inquiries</p>
                </div>

                <div className="bg-red-50 border-l-4 border-red-600 p-6 rounded-r-lg">
                  <h4 className="font-bold text-gray-900 mb-2 flex items-center gap-2">
                    <i className="ri-scales-line text-red-600"></i>
                    To Comply With Legal Obligations
                  </h4>
                  <p className="text-gray-700">Respond to legal requests, comply with regulations, and protect our legal rights</p>
                </div>
              </div>
            </div>

            {/* Information Sharing */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">5. How We Share Your Information</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We may share your information in the following circumstances:
              </p>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <i className="ri-group-line text-[#14B8A6] text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">With Other Users:</strong>
                    <span className="text-gray-700"> When you book a space or accept a booking, we share relevant information (name, profile photo, reviews) with the other party</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-service-line text-[#14B8A6] text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">With Service Providers:</strong>
                    <span className="text-gray-700"> We share information with third-party vendors who help us operate our platform (payment processors, hosting services, analytics providers)</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-shield-line text-[#14B8A6] text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">For Safety and Legal Reasons:</strong>
                    <span className="text-gray-700"> We may disclose information to comply with legal obligations, protect our rights, or respond to emergencies</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-building-line text-[#14B8A6] text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">Business Transfers:</strong>
                    <span className="text-gray-700"> If Stashly is involved in a merger, acquisition, or sale of assets, your information may be transferred</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-line text-[#14B8A6] text-xl mt-1"></i>
                  <div>
                    <strong className="text-gray-900">With Your Consent:</strong>
                    <span className="text-gray-700"> We may share information for other purposes with your explicit consent</span>
                  </div>
                </li>
              </ul>
            </div>

            {/* Data Security */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">6. Data Security</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We implement appropriate technical and organizational measures to protect your personal information:
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-6 rounded-xl">
                  <i className="ri-lock-line text-3xl text-[#14B8A6] mb-3"></i>
                  <h4 className="font-bold text-gray-900 mb-2">Encryption</h4>
                  <p className="text-gray-700 text-sm">All data transmitted between your device and our servers is encrypted using SSL/TLS</p>
                </div>
                <div className="bg-gray-50 p-6 rounded-xl">
                  <i className="ri-shield-check-line text-3xl text-[#14B8A6] mb-3"></i>
                  <h4 className="font-bold text-gray-900 mb-2">Secure Storage</h4>
                  <p className="text-gray-700 text-sm">Personal data is stored on secure servers with restricted access</p>
                </div>
                <div className="bg-gray-50 p-6 rounded-xl">
                  <i className="ri-user-settings-line text-3xl text-[#14B8A6] mb-3"></i>
                  <h4 className="font-bold text-gray-900 mb-2">Access Controls</h4>
                  <p className="text-gray-700 text-sm">Only authorized personnel have access to personal information</p>
                </div>
                <div className="bg-gray-50 p-6 rounded-xl">
                  <i className="ri-refresh-line text-3xl text-[#14B8A6] mb-3"></i>
                  <h4 className="font-bold text-gray-900 mb-2">Regular Updates</h4>
                  <p className="text-gray-700 text-sm">We regularly update our security practices and systems</p>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed mt-4">
                However, no method of transmission over the internet is 100% secure. While we strive to protect your information, we cannot guarantee absolute security.
              </p>
            </div>

            {/* Your Rights */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">7. Your Privacy Rights</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Depending on your location, you may have the following rights regarding your personal information:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-eye-line text-blue-600 text-xl mt-1"></i>
                  <span className="text-gray-700"><strong>Access:</strong> Request a copy of the personal information we hold about you</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-edit-line text-blue-600 text-xl mt-1"></i>
                  <span className="text-gray-700"><strong>Correction:</strong> Request correction of inaccurate or incomplete information</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-delete-bin-line text-blue-600 text-xl mt-1"></i>
                  <span className="text-gray-700"><strong>Deletion:</strong> Request deletion of your personal information</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-download-line text-blue-600 text-xl mt-1"></i>
                  <span className="text-gray-700"><strong>Portability:</strong> Request a copy of your data in a portable format</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-forbid-line text-blue-600 text-xl mt-1"></i>
                  <span className="text-gray-700"><strong>Objection:</strong> Object to certain processing of your information</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-close-circle-line text-blue-600 text-xl mt-1"></i>
                  <span className="text-gray-700"><strong>Withdraw Consent:</strong> Withdraw consent for processing where we rely on consent</span>
                </li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                To exercise these rights, please contact us at privacy@stashly.com. We will respond to your request within 30 days.
              </p>
            </div>

            {/* Cookies */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">8. Cookies and Tracking Technologies</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We use cookies and similar tracking technologies to enhance your experience on our platform:
              </p>
              <div className="space-y-4">
                <div className="bg-gray-50 p-6 rounded-xl">
                  <h4 className="font-bold text-gray-900 mb-2">Essential Cookies</h4>
                  <p className="text-gray-700">Required for the platform to function properly (authentication, security)</p>
                </div>
                <div className="bg-gray-50 p-6 rounded-xl">
                  <h4 className="font-bold text-gray-900 mb-2">Performance Cookies</h4>
                  <p className="text-gray-700">Help us understand how users interact with our platform</p>
                </div>
                <div className="bg-gray-50 p-6 rounded-xl">
                  <h4 className="font-bold text-gray-900 mb-2">Functionality Cookies</h4>
                  <p className="text-gray-700">Remember your preferences and settings</p>
                </div>
                <div className="bg-gray-50 p-6 rounded-xl">
                  <h4 className="font-bold text-gray-900 mb-2">Marketing Cookies</h4>
                  <p className="text-gray-700">Used to deliver relevant advertisements and track campaign effectiveness</p>
                </div>
              </div>
              <p className="text-gray-700 leading-relaxed mt-4">
                You can control cookies through your browser settings. However, disabling certain cookies may affect platform functionality.
              </p>
            </div>

            {/* Data Retention */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">9. Data Retention</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                We retain your personal information for as long as necessary to:
              </p>
              <ul className="space-y-3">
                <li className="flex items-start gap-3">
                  <i className="ri-time-line text-gray-600 text-xl mt-1"></i>
                  <span className="text-gray-700">Provide our services and maintain your account</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-time-line text-gray-600 text-xl mt-1"></i>
                  <span className="text-gray-700">Comply with legal obligations and resolve disputes</span>
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-time-line text-gray-600 text-xl mt-1"></i>
                  <span className="text-gray-700">Enforce our agreements and protect our legal rights</span>
                </li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                When you delete your account, we will delete or anonymize your personal information within 90 days, except where we are required to retain it for legal purposes.
              </p>
            </div>

            {/* Children's Privacy */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">10. Children's Privacy</h2>
              <p className="text-gray-700 leading-relaxed">
                Stashly is not intended for users under the age of 18. We do not knowingly collect personal information from children. If you believe we have collected information from a child, please contact us immediately at privacy@stashly.com.
              </p>
            </div>

            {/* International Transfers */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">11. International Data Transfers</h2>
              <p className="text-gray-700 leading-relaxed">
                Your information may be transferred to and processed in countries other than your country of residence. These countries may have different data protection laws. We ensure appropriate safeguards are in place to protect your information in accordance with this Privacy Policy.
              </p>
            </div>

            {/* Changes to Policy */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">12. Changes to This Privacy Policy</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update this Privacy Policy from time to time. We will notify you of significant changes by email or through a prominent notice on our platform. Your continued use of Stashly after changes are posted constitutes acceptance of the updated policy.
              </p>
            </div>

            {/* Contact */}
            <div className="mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">13. Contact Us</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                If you have questions or concerns about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="bg-gradient-to-br from-[#14B8A6] to-[#0F9B8E] text-white rounded-xl p-8">
                <h4 className="text-2xl font-bold mb-4">Privacy Team</h4>
                <p className="mb-2"><i className="ri-mail-line mr-2"></i> privacy@stashly.com</p>
                <p className="mb-2"><i className="ri-phone-line mr-2"></i> 1-800-STASHLY</p>
                <p><i className="ri-map-pin-line mr-2"></i> 123 Storage Street, Sydney NSW 2000, Australia</p>
              </div>
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
